import React from "react";
import Table from "@cloudscape-design/components/table";
import Header from "@cloudscape-design/components/header";
import Button from "@cloudscape-design/components/button";
import Box from "@cloudscape-design/components/box";
import Alert from "@cloudscape-design/components/alert";
import Toggle from "@cloudscape-design/components/toggle";
import Badge from "@cloudscape-design/components/badge";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Tabs from "@cloudscape-design/components/tabs";
import FormField from "@cloudscape-design/components/form-field";
import Input from "@cloudscape-design/components/input";
import Select from "@cloudscape-design/components/select";
import DateRangePicker from "@cloudscape-design/components/date-range-picker";
import Container from "@cloudscape-design/components/container";
import Grid from "@cloudscape-design/components/grid";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { PROMO_BONUS_TRANSLATION_TYPES, promoBonusEditModalStore, promoBonusesStore } from "../stores/promoBonuses";
import PromoBonusEditModal from "./PromoBonusEditModal";
import EcorAirdropSettingsModal from "./EcorAirdropSettingsModal";

const toSelectOption = (value) => {
	const normalized = value || "local";
	const option = PROMO_BONUS_TRANSLATION_TYPES.find((item) => item.value === normalized);
	return option || { value: normalized, label: normalized };
};
const minorToRub = (value) => {
	const amount = Number(value || 0) / 100;
	return Number.isInteger(amount) ? String(amount) : String(amount.toFixed(2));
};

const DATE_RANGE_I18N = {
	todayAriaLabel: "Today",
	nextMonthAriaLabel: "Next month",
	previousMonthAriaLabel: "Previous month",
	customRelativeRangeDurationLabel: "Duration",
	customRelativeRangeDurationPlaceholder: "Enter duration",
	customRelativeRangeOptionLabel: "Custom range",
	customRelativeRangeOptionDescription: "Set a custom range in the past",
	customRelativeRangeUnitLabel: "Unit",
	formatRelativeRange: (value) => value,
	formatUnit: (unit, value) => `${value} ${unit}`,
	dateTimeConstraintText: "Use YYYY-MM-DD.",
	relativeModeTitle: "Relative range",
	absoluteModeTitle: "Absolute range",
	relativeRangeSelectionHeading: "Choose a range",
	startDateLabel: "Start date",
	endDateLabel: "End date",
	startTimeLabel: "Start time",
	endTimeLabel: "End time",
	clearButtonLabel: "Clear",
	cancelButtonLabel: "Cancel",
	applyButtonLabel: "Apply",
};

class PromoBonuses extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			activeTabId: "list",
			showAirdropSettings: false,
			statsFilters: {
				bonusKey: { label: "All", value: "all" },
				contactType: { label: "All", value: "all" },
				dateRange: {
					type: "absolute",
					startDate: "",
					endDate: "",
				},
				kind: { label: "All", value: "all" },
				maxIssuedAmountRub: "",
				maxTargetActions: "",
				minIssuedAmountRub: "",
				minTargetActions: "",
			},
		};
	}

	componentDidMount() {
		this.props.Interface.loadPromoBonuses();
		this.loadStatistics();
	}

	getStatisticsFiltersPayload = () => {
		const { statsFilters } = this.state;
		const parseCount = (value) => {
			const parsed = Number.parseInt(value, 10);
			return Number.isFinite(parsed) ? parsed : null;
		};
		const rubToMinor = (value) => {
			const parsed = Number.parseFloat(String(value).replace(",", "."));
			return Number.isFinite(parsed) ? Math.round(parsed * 100) : null;
		};

		return {
			bonusKey: statsFilters.bonusKey.value === "all" ? "" : statsFilters.bonusKey.value,
			contactType: statsFilters.contactType.value === "all" ? "" : statsFilters.contactType.value,
			dateFrom: statsFilters.dateRange?.startDate ? `${statsFilters.dateRange.startDate} 00:00:00` : "",
			dateTo: statsFilters.dateRange?.endDate ? `${statsFilters.dateRange.endDate} 23:59:59` : "",
			kind: statsFilters.kind.value === "all" ? "" : statsFilters.kind.value,
			maxIssuedAmount: rubToMinor(statsFilters.maxIssuedAmountRub),
			maxTargetActions: parseCount(statsFilters.maxTargetActions),
			minIssuedAmount: rubToMinor(statsFilters.minIssuedAmountRub),
			minTargetActions: parseCount(statsFilters.minTargetActions),
		};
	};

	loadStatistics = () => {
		this.props.Interface.loadPromoBonusesStatistics(this.getStatisticsFiltersPayload()).catch((error) => {
			const normalizedError = typeof error?.text === "undefined" ? { text: String(error) } : error;
			this.props.setPromoBonuses({ ...this.props.promoBonuses, error: normalizedError });
		});
	};

	handleStatisticsFilterKeyDown = (event) => {
		if (event.detail.key !== "Enter") return;
		event.preventDefault();
		this.loadStatistics();
	};

	normalizePositiveDecimalInput = (value) => {
		const normalized = String(value || "").replace(",", ".").replace(/[^\d.]/g, "");
		const [integerPart, ...decimalParts] = normalized.split(".");
		return decimalParts.length ? `${integerPart}.${decimalParts.join("")}` : integerPart;
	};

	normalizePositiveIntegerInput = (value) => String(value || "").replace(/[^\d]/g, "");

	openAirdropSettings = () => this.setState({ showAirdropSettings: true });

	closeAirdropSettings = () => this.setState({ showAirdropSettings: false });

	openEditModal = (item) => {
		this.props.setPromoBonusEditModal({
			...this.props.promoBonusEditModal,
			show: true,
			key: item.key,
			kind: item.kind,
			contactType: item.contactType || "",
			enabled: Boolean(item.enabled),
			sort: String(item.sort ?? ""),
			bannerUrl: item.bannerUrl || "",
			bannerPreviewUrl: item.bannerPreviewUrl || "",
			title: item.title || "",
			titleType: toSelectOption(item.titleType),
			subtitle: item.subtitle || "",
			subtitleType: toSelectOption(item.subtitleType),
			buttonText: item.buttonText || "",
			buttonTextType: toSelectOption(item.buttonTextType),
			linkUrl: item.linkUrl || "",
			amount: item.kind === "contactBonus" ? minorToRub(item.amount) : "",
			depositRequired: Boolean(item.depositRequired),
			depositFrom: item.kind === "contactBonus" ? minorToRub(item.depositFrom) : "",
		});
	};

	toggleEnabled = (item, enabled) => {
		const nextData = this.props.promoBonuses.data.map((bonus) => (bonus.key === item.key ? { ...bonus, enabled } : bonus));

		this.props.Interface.savePromoBonuses(nextData).catch((error) => {
			const normalizedError = typeof error.text === "undefined" ? { text: error.toString() } : error;
			this.props.setPromoBonuses({ ...this.props.promoBonuses, loaded: true, error: normalizedError });
		});
	};

	renderTextWithType = (text, type) => (
		<Box>
			<Box>{text}</Box>
			<Box variant="small">{type}</Box>
		</Box>
	);

	renderBannerPreview = (item) => {
		if (!item.bannerUrl) return "-";
		const previewUrl = item.bannerPreviewUrl || item.bannerUrl;

		return (
			<Box>
				<img
					alt={`${item.key} banner`}
					src={previewUrl}
					style={{
						width: "110px",
						height: "54px",
						objectFit: "cover",
						borderRadius: "4px",
						display: "block",
						background: "#f2f3f3",
					}}
					onError={(event) => {
						event.currentTarget.style.display = "none";
					}}
				/>
			</Box>
		);
	};

	setStatsFilter = (field, value) => {
		this.setState((prev) => ({
			...prev,
			statsFilters: {
				...prev.statsFilters,
				[field]: value,
			},
		}));
	};

	getBonusOptions = () => {
		const options = [{ label: "All", value: "all" }];
		this.props.promoBonuses.data.forEach((bonus) => {
			options.push({
				label: bonus.key,
				value: bonus.key,
			});
		});
		return options;
	};

	renderStatistics() {
		const { promoBonuses } = this.props;
		const { statsFilters } = this.state;
		const statistics = promoBonuses.statistics || { data: [], loaded: false, summary: {} };
		const summary = statistics.summary || {};

		return (
			<SpaceBetween size="m">
				<SpaceBetween size="m">
					<SpaceBetween direction="horizontal" size="m">
						<div style={{ width: 150 }}>
							<FormField label="Kind">
								<Select
									selectedOption={statsFilters.kind}
									onChange={({ detail }) => this.setStatsFilter("kind", detail.selectedOption)}
									options={[
										{ label: "All", value: "all" },
										{ label: "contactBonus", value: "contactBonus" },
										{ label: "static", value: "static" },
									]}
								/>
							</FormField>
						</div>
						<div style={{ width: 150 }}>
							<FormField label="Card" stretch>
								<Select selectedOption={statsFilters.bonusKey} onChange={({ detail }) => this.setStatsFilter("bonusKey", detail.selectedOption)} options={this.getBonusOptions()} />
							</FormField>
						</div>
						<div style={{ width: 150 }}>
							<FormField label="Contact type" stretch>
								<Select
									selectedOption={statsFilters.contactType}
									onChange={({ detail }) => this.setStatsFilter("contactType", detail.selectedOption)}
									options={[
										{ label: "All", value: "all" },
										{ label: "phone", value: "phone" },
										{ label: "email", value: "email" },
										{ label: "telegram", value: "telegram" },
									]}
								/>
							</FormField>
						</div>
					</SpaceBetween>
					<SpaceBetween direction="horizontal" size="m">
						<FormField label="Date range">
							<DateRangePicker value={statsFilters.dateRange} onChange={({ detail }) => this.setStatsFilter("dateRange", detail.value)} onKeyDown={this.handleStatisticsFilterKeyDown} i18nStrings={DATE_RANGE_I18N} dateOnly isValidRange={() => ({ valid: true })} placeholder="Select period" />
						</FormField>
						<FormField label="Min Bonus (RUB)">
							<Input type="number" inputMode="decimal" min="0" step="0.01" value={statsFilters.minIssuedAmountRub} onChange={({ detail }) => this.setStatsFilter("minIssuedAmountRub", this.normalizePositiveDecimalInput(detail.value))} onKeyDown={this.handleStatisticsFilterKeyDown} />
						</FormField>
						<FormField label="Max Bonus (RUB)">
							<Input type="number" inputMode="decimal" min="0" step="0.01" value={statsFilters.maxIssuedAmountRub} onChange={({ detail }) => this.setStatsFilter("maxIssuedAmountRub", this.normalizePositiveDecimalInput(detail.value))} onKeyDown={this.handleStatisticsFilterKeyDown} />
						</FormField>
						<FormField label="Min target actions">
							<Input type="number" inputMode="numeric" min="0" step="1" value={statsFilters.minTargetActions} onChange={({ detail }) => this.setStatsFilter("minTargetActions", this.normalizePositiveIntegerInput(detail.value))} onKeyDown={this.handleStatisticsFilterKeyDown} />
						</FormField>
						<FormField label="Max target actions">
							<Input type="number" inputMode="numeric" min="0" step="1" value={statsFilters.maxTargetActions} onChange={({ detail }) => this.setStatsFilter("maxTargetActions", this.normalizePositiveIntegerInput(detail.value))} onKeyDown={this.handleStatisticsFilterKeyDown} />
						</FormField>
					</SpaceBetween>
					<Box margin={{ top: "s" }}>
						<SpaceBetween direction="horizontal" size="m">
							<Button onClick={this.loadStatistics}>Apply filters</Button>
							<Button onClick={this.loadStatistics}>Refresh</Button>
						</SpaceBetween>
					</Box>
				</SpaceBetween>

				<Container header={<Header variant="h3">Summary</Header>}>
					<Grid gridDefinition={[{ colspan: 2 }, { colspan: 2 }, { colspan: 2 }, { colspan: 2 }, { colspan: 2 }, { colspan: 2 }]}>
						<Box>
							<Box variant="awsui-key-label">Views</Box>
							<Box variant="h3">{summary.views || 0}</Box>
						</Box>
						<Box>
							<Box variant="awsui-key-label">Clicks</Box>
							<Box variant="h3">{summary.clicks || 0}</Box>
						</Box>
						<Box>
							<Box variant="awsui-key-label">Target actions</Box>
							<Box variant="h3">{summary.targetActions || 0}</Box>
						</Box>
						<Box>
							<Box variant="awsui-key-label">View to click</Box>
							<Box variant="h3">{summary.viewToClick || 0}%</Box>
						</Box>
						<Box>
							<Box variant="awsui-key-label">Click to target</Box>
							<Box variant="h3">{summary.clickToTarget || 0}%</Box>
						</Box>
						<Box>
							<Box variant="awsui-key-label">Bonuses Accrued RUB</Box>
							<Box variant="h3">{summary.issuedAmountRub || 0}</Box>
						</Box>
					</Grid>
				</Container>

				<Table
					stickyHeader
					trackBy="bonusKey"
					loading={!statistics.loaded}
					columnDefinitions={[
						{ id: "bonusKey", header: "Card", cell: (item) => item.bonusKey },
						{ id: "kind", header: "Kind", cell: (item) => item.kind },
						{ id: "contactType", header: "Contact type", cell: (item) => item.contactType || "-" },
						{ id: "views", header: "Unique views", cell: (item) => item.views },
						{ id: "clicks", header: "Clicks", cell: (item) => item.clicks },
						{ id: "viewToClick", header: "Views to clicks", cell: (item) => `${item.viewToClick}%` },
						{ id: "targetActions", header: "Target actions", cell: (item) => item.targetActions },
						{ id: "clickToTarget", header: "Clicks to target", cell: (item) => `${item.clickToTarget}%` },
						{ id: "issuedAmountRub", header: "Bonus RUB", cell: (item) => item.issuedAmountRub },
					]}
					items={statistics.data || []}
					header={<Header counter={`(${(statistics.data || []).length})`}>Promo bonuses statistics</Header>}
					empty="No statistics found"
				/>
			</SpaceBetween>
		);
	}

	render() {
		const { promoBonuses } = this.props;
		const errorText = promoBonuses.error?.text || "";

		return (
			<>
				<PromoBonusEditModal Interface={this.props.Interface} styles={this.props.styles} />
				<EcorAirdropSettingsModal
					Interface={this.props.Interface}
					visible={this.state.showAirdropSettings}
					onDismiss={this.closeAirdropSettings}
				/>

				{promoBonuses.loaded && errorText ? (
					<Box margin={{ bottom: "m" }}>
						<Alert type="error" header="Error loading data:">
							<Box variant="p">{errorText}</Box>
							{promoBonuses.error.code ? (
								<>
									<hr />
									<Box variant="p">
										request error code: {promoBonuses.error.code} | request timestamp: {promoBonuses.error.timestamp}
									</Box>
								</>
							) : null}
						</Alert>
					</Box>
				) : null}

				<Tabs
					activeTabId={this.state.activeTabId}
					onChange={({ detail }) => {
						this.setState({ activeTabId: detail.activeTabId });
					}}
					tabs={[
						{
							id: "list",
							label: "Bonuses list",
							content: (
								<Table
									stickyHeader
									trackBy="key"
									loading={!promoBonuses.loaded}
									columnDefinitions={[
										{
											id: "key",
											header: "Key",
											cell: (item) => (
												<Button className={[this.props.styles["btnUID"], typeof document.body.classList[0] !== "undefined" ? this.props.styles["dark"] : "", this.props.styles["blue"]].join(" ")} variant="normal" onClick={() => this.openEditModal(item)}>
													{item.key}
												</Button>
											),
										},
										{ id: "kind", header: "Kind", cell: (item) => item.kind },
										{
											id: "enabled",
											header: "Enabled",
											cell: (item) => (
												<Toggle checked={Boolean(item.enabled)} disabled={!promoBonuses.loaded} onChange={({ detail }) => this.toggleEnabled(item, detail.checked)}>
													{item.enabled ? "On" : "Off"}
												</Toggle>
											),
										},
										{ id: "bannerUrl", header: "Banner", cell: (item) => this.renderBannerPreview(item) },
										{ id: "sort", header: "Sort", cell: (item) => item.sort },
										{ id: "amount", header: "Amount RUB", cell: (item) => (item.kind === "contactBonus" ? minorToRub(item.amount) : "-") },
										{ id: "depositRequired", header: "Deposit", cell: (item) => <span style={{ whiteSpace: "nowrap" }}>{item.kind === "contactBonus" ? <Badge color={item.depositRequired ? "blue" : "grey"}>{item.depositRequired ? "Required" : "Not required"}</Badge> : "-"}</span> },
										{ id: "depositFrom", header: "Deposit From RUB", cell: (item) => (item.kind === "contactBonus" ? minorToRub(item.depositFrom) : "-") },
										{ id: "linkUrl", header: "Link URL", cell: (item) => item.linkUrl },
										{
											id: "actions",
											header: "Actions",
											minWidth: 150,
											cell: (item) => (
												<Button disabled={!promoBonuses.loaded} onClick={() => this.openEditModal(item)}>
													Edit
												</Button>
											),
										},
									]}
									items={promoBonuses.data}
									header={
										<Header
											counter={`(${promoBonuses.data.length})`}
										actions={
											<SpaceBetween direction="horizontal" size="m">
												<Button onClick={this.openAirdropSettings}>
													ECOR Air-drop settings
												</Button>
												<Button disabled={!promoBonuses.loaded} onClick={() => this.props.Interface.loadPromoBonuses()}>
													Refresh
												</Button>
											</SpaceBetween>
											}>
											Promo bonuses
										</Header>
									}
									empty="No promo bonuses found"
								/>
							),
						},
						{
							id: "statistics",
							label: "Statistics",
							content: this.renderStatistics(),
						},
					]}
				/>
			</>
		);
	}
}

function withRecoil(Component) {
	return function WrappedComponent(props) {
		const promoBonuses = useRecoilValue(promoBonusesStore);
		const setPromoBonuses = useSetRecoilState(promoBonusesStore);
		const promoBonusEditModal = useRecoilValue(promoBonusEditModalStore);
		const setPromoBonusEditModal = useSetRecoilState(promoBonusEditModalStore);

		return <Component {...props} promoBonuses={promoBonuses} setPromoBonuses={setPromoBonuses} promoBonusEditModal={promoBonusEditModal} setPromoBonusEditModal={setPromoBonusEditModal} />;
	};
}

export default withRecoil(PromoBonuses);
